import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Ruler, Search } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSizeChartPage = () => {
  const [sizeCharts, setSizeCharts] = useState({
    jacket: [],
    pant: [],
    vest: [],
    shirt: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("jacket");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    fetchSizeCharts();
  }, [navigate]);

  const fetchSizeCharts = async () => {
    try {
      const response = await axios.get(`${API}/size-chart/all`);
      setSizeCharts(response.data);
    } catch (error) {
      console.error("Error fetching size charts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = (data) => {
    if (!searchQuery) return data;
    return data.filter(item => 
      item.size.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const renderJacketTable = () => {
    const data = getFilteredData(sizeCharts.jacket);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 font-medium text-primary">Size</th>
              <th className="text-left py-3 px-4 font-medium">Chest</th>
              <th className="text-left py-3 px-4 font-medium">Stomach</th>
              <th className="text-left py-3 px-4 font-medium">Seat</th>
              <th className="text-left py-3 px-4 font-medium">Shoulder</th>
              <th className="text-left py-3 px-4 font-medium">Bicep</th>
              <th className="text-left py-3 px-4 font-medium">Sleeve</th>
              <th className="text-left py-3 px-4 font-medium">Back L.</th>
              <th className="text-left py-3 px-4 font-medium">Front L.</th>
              <th className="text-left py-3 px-4 font-medium">Cuff</th>
              <th className="text-left py-3 px-4 font-medium text-amber-400">Button Stance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <motion.tr 
                key={item.size}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-white/5 hover:bg-white/5"
              >
                <td className="py-3 px-4 font-medium text-primary">{item.size}</td>
                <td className="py-3 px-4 text-muted-foreground">{item.chest}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.stomach}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.seat}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.shoulder}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.bicep}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.sleeve_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.back_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.front_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.cuff}"</td>
                <td className="py-3 px-4 text-amber-400 font-medium">{item.button_stance}"</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPantTable = () => {
    const data = getFilteredData(sizeCharts.pant);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 font-medium text-primary">Size</th>
              <th className="text-left py-3 px-4 font-medium">Waist</th>
              <th className="text-left py-3 px-4 font-medium">Seat</th>
              <th className="text-left py-3 px-4 font-medium">U-Rise</th>
              <th className="text-left py-3 px-4 font-medium">Thigh</th>
              <th className="text-left py-3 px-4 font-medium">Length</th>
              <th className="text-left py-3 px-4 font-medium">Knee</th>
              <th className="text-left py-3 px-4 font-medium">Bottom</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <motion.tr 
                key={item.size}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-white/5 hover:bg-white/5"
              >
                <td className="py-3 px-4 font-medium text-primary">{item.size}</td>
                <td className="py-3 px-4 text-muted-foreground">{item.waist}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.seat}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.urise}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.thigh}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.pant_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.knee}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.bottom}"</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVestTable = () => {
    const data = getFilteredData(sizeCharts.vest);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 font-medium text-primary">Size</th>
              <th className="text-left py-3 px-4 font-medium">Chest</th>
              <th className="text-left py-3 px-4 font-medium">Stomach</th>
              <th className="text-left py-3 px-4 font-medium">Back L.</th>
              <th className="text-left py-3 px-4 font-medium">Front L.</th>
              <th className="text-left py-3 px-4 font-medium">Bottom</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <motion.tr 
                key={item.size}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-white/5 hover:bg-white/5"
              >
                <td className="py-3 px-4 font-medium text-primary">{item.size}</td>
                <td className="py-3 px-4 text-muted-foreground">{item.chest}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.stomach}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.back_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.front_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.bottom}"</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderShirtTable = () => {
    const data = getFilteredData(sizeCharts.shirt);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 font-medium text-primary">Size</th>
              <th className="text-left py-3 px-4 font-medium">Neck</th>
              <th className="text-left py-3 px-4 font-medium">Chest</th>
              <th className="text-left py-3 px-4 font-medium">Waist</th>
              <th className="text-left py-3 px-4 font-medium">Seat</th>
              <th className="text-left py-3 px-4 font-medium">Shoulders</th>
              <th className="text-left py-3 px-4 font-medium">Shirt Length</th>
              <th className="text-left py-3 px-4 font-medium">Sleeve</th>
              <th className="text-left py-3 px-4 font-medium">Cuff</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <motion.tr 
                key={item.size}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-white/5 hover:bg-white/5"
              >
                <td className="py-3 px-4 font-medium text-primary">{item.size}</td>
                <td className="py-3 px-4 text-muted-foreground">{item.neck}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.chest}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.waist}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.seat}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.shoulders ?? item.yoke}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.shirt_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.sleeve_length}"</td>
                <td className="py-3 px-4 text-muted-foreground">{item.cuff}"</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Ruler className="text-primary" size={28} />
            <h1 className="font-serif text-3xl tracking-tight">Size Chart Reference</h1>
          </div>
          <p className="text-muted-foreground">
            Standard gauge measurements for jacket, pant, vest, and shirt sizing
          </p>
        </div>
      </section>

      {/* Size Charts */}
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-muted-foreground">Loading size charts...</p>
          ) : (
            <div className="space-y-6">
              {/* Search */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input
                    placeholder="Search by size..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-card border-white/10"
                  />
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-card border border-white/10">
                  <TabsTrigger value="jacket" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    Jacket ({sizeCharts.jacket.length})
                  </TabsTrigger>
                  <TabsTrigger value="pant" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    Pant ({sizeCharts.pant.length})
                  </TabsTrigger>
                  <TabsTrigger value="vest" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    Vest ({sizeCharts.vest.length})
                  </TabsTrigger>
                  <TabsTrigger value="shirt" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    Shirt ({sizeCharts.shirt.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="jacket" className="mt-6">
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-medium">Jacket Size Chart</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        S = Short, R = Regular, L = Long. All measurements in inches.
                      </p>
                    </div>
                    {renderJacketTable()}
                  </div>
                </TabsContent>

                <TabsContent value="pant" className="mt-6">
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-medium">Pant Size Chart</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Based on waist measurement. All measurements in inches.
                      </p>
                    </div>
                    {renderPantTable()}
                  </div>
                </TabsContent>

                <TabsContent value="vest" className="mt-6">
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-medium">Vest Size Chart</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Based on chest measurement. All measurements in inches.
                      </p>
                    </div>
                    {renderVestTable()}
                  </div>
                </TabsContent>

                <TabsContent value="shirt" className="mt-6">
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-medium">Shirt Size Chart</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Based on neck measurement. All measurements in inches.
                      </p>
                    </div>
                    {renderShirtTable()}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminSizeChartPage;
